import React, { useState, useContext, useEffect, useRef, useMemo } from "react"
import { Box, CircularProgress, FormLabel } from "@mui/joy";
import { useDrawer } from "../Drawer";
import { Button } from '../Button';
import Relationship from "../RelationshipElements/Relationship";
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';

import { useTranslation } from 'react-i18next'
import { useDataProvider } from "src/mtska-frontend-data-provider";

/** IDs for async relationship hydration (strings or {_id|id} objects). */
const normalizeRelationshipIds = (val) => {
    if (val == null) {
        return [];
    }
    const arr = Array.isArray(val) ? val : [val];
    return arr
        .map((item) => {
            if (item == null) {
                return null;
            }
            if (typeof item === 'object') {
                return item._id ?? item.id ?? null;
            }
            return item;
        })
        .filter(Boolean);
};

/** Max IDs per GET request before chunking (nginx/apache often limit ~8k URI). */
const MAX_IDS_PER_REQUEST = 25;

const chunkArray = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size));
    }
    return out;
};

const hasEmbeddedRelationshipObjects = (val) => {
    if (!Array.isArray(val) || val.length === 0) {
        return false;
    }
    const first = val[0];
    return typeof first === 'object' && first != null && (
        first.name != null
        || first.slug != null
        || first.firstname != null
        || first.car != null
        || first.assignment_start_at != null
        || first.plate != null
        || first.finalValue != null
        || first.code != null
    );
};

const RelationshipManager = ({ dataProviderName, detailStack = {}, modelName, parentitem, relation, editing, required, name, value, label, view, disabled, readonly, className = "", multiple, setValue, checkable, checked, onCheckChange, mirrorField }) => {

    if (!editing) readonly = true;

    const [error, setError] = useState();

    const { activate } = useDrawer();
    const { t } = useTranslation();
    const { userCapabilities } = useContext(AppContext);
    const [loadingDissociation, setLoadingDissociation] = useState(false);
    const isAsync = Boolean(relation?.dataProviderName);
    const relationshipIds = useMemo(() => normalizeRelationshipIds(value), [value]);
    const embeddedObjects = useMemo(() => hasEmbeddedRelationshipObjects(value), [value]);
    const parentId = parentitem?._id ?? parentitem?.id;
    const parentLoadSpec = useMemo(() => {
        if (!relation?.asyncParentField) {
            return null;
        }
        return {
            field: relation.asyncParentField,
            operator: relation.asyncParentOperator ?? 'equals',
        };
    }, [relation?.asyncParentField, relation?.asyncParentOperator]);
    const canLoadByParent = Boolean(isAsync && parentLoadSpec && parentId);
    const [drawable, setDrawable] = useState(() => (!isAsync ? true : (embeddedObjects ? true : null)));
    const [hydratedValue, setHydratedValue] = useState(() => (embeddedObjects ? value : null));
    const [asyncLoading, setAsyncLoading] = useState(
        () => isAsync && (relationshipIds.length > 0 || canLoadByParent) && !embeddedObjects
    );
    const loaded = useRef(false);

    const {
        doAction
    } = useDataProvider(dataProviderName, false);

    const {
        loadAll: loadAllAsync,
        loading: asyncProviderLoading,
    } = useDataProvider(relation.dataProviderName, false);

    useEffect(() => {
        if (!isAsync) {
            setHydratedValue(value);
            setDrawable(true);
            setAsyncLoading(false);
            return;
        }

        if (embeddedObjects) {
            setHydratedValue(value);
            setDrawable(true);
            setAsyncLoading(false);
            return;
        }

        const parentStamp = parentitem?.updated_at ?? parentitem?._id ?? parentId;
        let filterItems;
        let loadKey;

        const loadOptions = { forceRelation: true };
        const extraFilters = relation.filter?.items ?? [];

        if (canLoadByParent) {
            const { field, operator } = parentLoadSpec;
            const parentValue = operator === 'isAnyOf' ? [parentId] : parentId;
            filterItems = [
                { field, operator, value: parentValue },
                ...extraFilters,
            ];
            loadKey = `parent:${parentId}:${name}:${JSON.stringify(relation.filter ?? {})}:${parentStamp}`;
        } else if (relationshipIds.length > 0) {
            filterItems = [
                {
                    field: "id",
                    operator: "isAnyOf",
                    value: relationshipIds,
                },
            ];
            loadKey = `ids:${relationshipIds.length}:${relationshipIds[0]}:${relationshipIds[relationshipIds.length - 1]}:${name}`;
        } else {
            setHydratedValue([]);
            setDrawable('nodata');
            setAsyncLoading(false);
            loaded.current = false;
            return;
        }

        if (loaded.current === loadKey) {
            return;
        }
        loaded.current = loadKey;

        setAsyncLoading(true);
        setDrawable(null);
        setHydratedValue(null);

        const runLoad = () => {
            if (canLoadByParent || relationshipIds.length <= MAX_IDS_PER_REQUEST) {
                return loadAllAsync(
                    { items: filterItems },
                    null,
                    null,
                    null,
                    loadOptions,
                );
            }
            const idChunks = chunkArray(relationshipIds, MAX_IDS_PER_REQUEST);
            return Promise.all(
                idChunks.map((chunk) => loadAllAsync(
                    { items: [{ field: 'id', operator: 'isAnyOf', value: chunk }] },
                    null,
                    null,
                    null,
                    loadOptions,
                )),
            ).then((responses) => {
                const merged = [];
                const seen = new Set();
                for (const response of responses) {
                    for (const row of response.items?.data ?? []) {
                        const rowId = row._id ?? row.id;
                        if (rowId != null && seen.has(rowId)) {
                            continue;
                        }
                        if (rowId != null) {
                            seen.add(rowId);
                        }
                        merged.push(row);
                    }
                }
                return { items: { data: merged } };
            });
        };

        runLoad().then((response) => {
            const data = response.items?.data ?? [];
            if (data?.length > 0) {
                setHydratedValue(data);
                setDrawable(true);
            } else {
                setDrawable('nodata');
            }
        }).catch(() => {
            setDrawable('nodata');
        }).finally(() => {
            setAsyncLoading(false);
        });
    }, [isAsync, embeddedObjects, relationshipIds, canLoadByParent, parentLoadSpec, parentId, parentitem, name, loadAllAsync, value, relation.filter]);

    useEffect(() => {
        //console.info('RELATIONSHIP CAMBIA VALUE', name, value)
    }, [ value]);

    let generalClass = "form-element-control";
    generalClass += (editing) ? " editing" : " reading";
    generalClass += (required) ? " required" : "";
    generalClass += (disabled) ? " disabled" : "";
    generalClass += (readonly) ? " readonly" : "";

    const onChange = () => {

    }

    const handleItemRemove = (childitem) => {
        if (typeof childitem === 'undefined') return;
        //console.info("relationship handleItemRemove", childitem);

        if (relation.relationDissociator) {
            activate(relation.relationDissociator, {
                defaultValue: childitem,
                defaultSelectedValues: childitem._id,
                enableSelection: true,
                disableMultipleRowSelection: !multiple,
                parent: { ...parentitem },
                filters: { ...relation.filter }
            }, () => { })
        } else {
            setLoadingDissociation(true);
            doAction && doAction({
                action: 'relationshipDissociation',
                model: dataProviderName,
                data: {
                    relationName: name,
                    parentId: parentitem._id,
                    childrenId: [childitem._id],
                }
            }).then(() => {
                setLoadingDissociation(false);
                console.info('relationshipDissociation completed')
            });
        }
    }

    const handleItemDetailOpen = (v) => {
        activate(relation.drawerRoute + '/detail', {
            id: v._id,
            defaultValue: v,
            defaultSelectedValues: [],
            enableSelection: true,
            filters: v,
            detailStack: {
                inverseRelation: { [relation.inverseRelation]: parentitem },
                inverseRelationType: relation.inverseRelationType,
                stack: [...(detailStack.stack ?? []), { [relation.inverseRelation]: parentitem }]
            }
        }, onChange);
    }

    if (!relation.hasOwnProperty('limit')) relation.limit = 1000;

    const showAsyncLoading = isAsync && (asyncLoading || asyncProviderLoading) && drawable !== 'nodata';

    return (
        <div className={("form-element relationship " + className).trim()}>
            {/* {multiple ? "multiple" : "single"} */}
            <Box className={(error) ? generalClass + " form-element-error" : generalClass}>
                <Box className="form-element-relationship-value">
                    <FormLabel className="form-element-label" htmlFor={name}>{t(label)} {mirrorField}</FormLabel>
                </Box>
               
                {/* <JSONPretty data={drawable} className="json-wrapper"></JSONPretty> */}
                {/* <JSONPretty data={relation} className="json-wrapper"></JSONPretty> */}
                {/* <JSONPretty data={value} className="json-wrapper"></JSONPretty> */}
                {/* <JSONPretty data={hydratedValue} className="json-wrapper"></JSONPretty> */}
                {/* <JSONPretty data={{ "isAsync": isAsync }} className="json-wrapper"></JSONPretty>  */}

                {
                    showAsyncLoading || drawable == null ?
                        <Box style={{height:'72px',marginBottom:'10px'}} className="form-element-relationship-options form-element-relationship-loading"><CircularProgress size="sm" /></Box> :
                        drawable === true ?
                            <Box className="form-element-relationship-options">
                                {
                                    /*
                                        relation.inverseRelation ? <Alert severity="success" sx={{fontSize:'10px',backgroundColor:'#DAFFAE',padding:'3px',marginBottom:'3px'}}>Inverse Relation Defined</Alert> : <Alert severity="error" sx={{fontSize:'10px',backgroundColor:'#FFD8FD',padding:'3px',marginBottom:'3px'}}>Inverse Relation Missing</Alert>
                                    */
                                }
                                <Relationship
                                    view={view}
                                    value={hydratedValue}
                                    name={name}
                                    filters={parentitem}
                                    relation={relation}
                                    callback={onChange}
                                    editing={editing}
                                    visiblelimit={relation.visiblelimit}
                                    detailInspector={relation.detailInspector != 'hidden' || userCapabilities['auth/role.is_driver'] ? true : false}
                                    handleItemDetail={handleItemDetailOpen}
                                    handleItemRemove={relation.relationDissociator != 'hidden' && !userCapabilities['auth/role.is_driver'] && !readonly ? handleItemRemove : null}
                                    setValue={setValue}
                                    checkable={checkable}
                                    checked={checked}
                                    onCheckChange={onCheckChange}
                                    loadingDissociation={loadingDissociation}
                                />
                                {((!Array.isArray(hydratedValue) || relation.limit > hydratedValue.length) && relation.relationAssociator != 'hidden' && !readonly) && (
                                    <Box className="relationship-actions">
                                        <Button
                                            size="medium"
                                            onClick={() => {
                                                if (relation.relationAssociator == 'hidden') return;
                                                return activate(relation.relationAssociator ?? relation.drawerRoute + '/list', {
                                                    defaultValue: hydratedValue,
                                                    defaultSelectedValues: hydratedValue,
                                                    enableSelection: true,
                                                    disableMultipleRowSelection: !multiple,
                                                    filters: { ...relation.filter },
                                                    intent: "relationshipManagement",
                                                    relation: {
                                                        name: name,
                                                        dataProviderName: dataProviderName,
                                                        parentItemID: parentitem._id,
                                                        doAction: doAction
                                                    },
                                                    detailStack: {
                                                        inverseRelation: { [relation.inverseRelation]: parentitem },
                                                        inverseRelationType: relation.inverseRelationType,
                                                        stack: [...(detailStack.stack ?? []), { [relation.inverseRelation]: parentitem }]
                                                    }
                                                }, onChange)
                                            }}
                                            //label={multiple ? t("select many {{drawerRoute}}", { drawerRoute: relation.drawerRoute }) : t("select a {{drawerRoute}}", { drawerRoute: relation.drawerRoute })}
                                            label={multiple ? t("select") : t("select")}
                                            icon={multiple ? "faListCheck" : "faListRadio"}
                                            variant="outlined"
                                            className="relationship-activator"
                                            disabled={readonly}
                                        />
                                    </Box>
                                )}
                            </Box>
                            :
                            <Box style={{height:'72px',marginBottom:'10px',padding:'25px',backgroundColor:'var(--neutral-050)',borderRadius:'10px', color:'var(--neutral-400)'}}>No content</Box>
                }
            </Box>
        </div>
    )
}

export default RelationshipManager
